import type { VariantQuantityBreak } from 'services/product.service';

export function findApplicableQuantityBreak(
  tiers: VariantQuantityBreak[] | undefined,
  quantity: number,
): VariantQuantityBreak | undefined {
  if (!tiers?.length || quantity <= 0) {
    return undefined;
  }

  const matches = tiers.filter(
    (tier) => quantity >= tier.quantityFrom && quantity <= tier.quantityTo,
  );

  if (matches.length === 0) {
    return undefined;
  }

  return matches.reduce((best, tier) =>
    tier.quantityFrom > best.quantityFrom ? tier : best,
  );
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export function resolveQuantityBreakUnitPrice(
  baseExVat: number,
  baseIncVat: number,
  tier: VariantQuantityBreak | undefined,
): { exVat: number; incVat: number; applied: boolean } {
  if (!tier || baseExVat <= 0) {
    return { exVat: baseExVat, incVat: baseIncVat, applied: false };
  }

  const fixedRsp = Number(tier.newRsp) || 0;
  const percent = Number(tier.percentDiscount) || 0;

  let exVat = baseExVat;
  if (fixedRsp > 0) {
    exVat = roundCurrency(fixedRsp);
  } else if (percent > 0) {
    exVat = roundCurrency(baseExVat * (1 - percent / 100));
  } else {
    return { exVat: baseExVat, incVat: baseIncVat, applied: false };
  }

  const ratio = baseExVat > 0 ? baseIncVat / baseExVat : 1;
  return { exVat, incVat: roundCurrency(exVat * ratio), applied: true };
}

export function applyQuantityBreakForQuantity(
  baseExVat: number,
  baseIncVat: number,
  tiers: VariantQuantityBreak[] | undefined,
  quantity: number,
): { exVat: number; incVat: number; applied: boolean; percentDiscount?: number } {
  const tier = findApplicableQuantityBreak(tiers, quantity);
  const resolved = resolveQuantityBreakUnitPrice(baseExVat, baseIncVat, tier);
  return {
    ...resolved,
    percentDiscount: resolved.applied && tier?.percentDiscount ? tier.percentDiscount : undefined,
  };
}

export function formatQuantityBreakTier(tier: VariantQuantityBreak): string {
  const from = tier.quantityFrom;
  const to = tier.quantityTo;

  if (tier.percentDiscount && tier.percentDiscount > 0) {
    if (to >= 999) {
      return `Buy ${from}+ for ${tier.percentDiscount}% off`;
    }
    if (from === to) {
      return `Buy ${from} for ${tier.percentDiscount}% off`;
    }
    return `Buy ${from}–${to} for ${tier.percentDiscount}% off`;
  }

  if (tier.discountedPriceExVat != null && tier.discountedPriceExVat > 0) {
    const price = tier.discountedPriceExVat.toFixed(2);
    return to >= 999 ? `Buy ${from}+ for £${price} each` : `Buy ${from}+ for £${price} each`;
  }

  return to >= 999 ? `Buy ${from}+ for a discounted price` : `Buy ${from}+ for a discounted price`;
}

/** Short label for product cards — uses the lowest quantity tier. */
export function formatQuantityBreaksSummary(breaks: VariantQuantityBreak[] | undefined): string | null {
  if (!breaks?.length) {
    return null;
  }
  return formatQuantityBreakTier(breaks[0]);
}

/** Pick the best promo label for the current cart quantity. */
export function formatActiveQuantityBreakLabel(
  breaks: VariantQuantityBreak[] | undefined,
  quantity: number,
): string | null {
  const tier = findApplicableQuantityBreak(breaks, quantity);
  if (!tier) {
    const nextTier = breaks?.find((t) => quantity < t.quantityFrom);
    if (nextTier?.percentDiscount) {
      const needed = nextTier.quantityFrom - quantity;
      return `Add ${needed} more for ${nextTier.percentDiscount}% off`;
    }
    return formatQuantityBreaksSummary(breaks);
  }
  return formatQuantityBreakTier(tier);
}
